import { useState, useEffect, useCallback, useRef } from "react";
import { MobileLayout } from "../../components/MobileLayout";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../lib/supabase";
import { BookOpen, CheckCircle2, Play, X, Trophy } from "lucide-react";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([A-Za-z0-9_-]{11})/
  );
  return m?.[1] || null;
}

export default function TreinamentoPage() {
  const { user } = useApp();

  const [treinamentos, setTreinamentos] = useState([]);
  const [progresso, setProgresso]       = useState({});
  const [loading, setLoading]           = useState(true);
  const [videoAberto, setVideoAberto]   = useState(null);

  // Refs so the single postMessage listener always sees current values
  const videoAbertoRef  = useRef(null);
  const progressoRef    = useRef({});
  const marcarRef       = useRef(null);
  videoAbertoRef.current = videoAberto;
  progressoRef.current   = progresso;

  const carregar = useCallback(async () => {
    const [resT, resP] = await Promise.all([
      supabase
        .from("liberty_treinamentos")
        .select("*")
        .eq("empresa_id", EMPRESA_ID)
        .eq("ativo", true)
        .order("ordem", { ascending: true }),
      user?.id
        ? supabase
            .from("liberty_treinamento_progresso")
            .select("treinamento_id, assistido")
            .eq("vigilante_id", user.id)
            .eq("empresa_id", EMPRESA_ID)
            .eq("assistido", true)
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (!resT.error) setTreinamentos(resT.data || []);
    if (!resP.error) {
      const map = {};
      for (const p of resP.data || []) map[p.treinamento_id] = true;
      setProgresso(map);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { carregar(); }, [carregar]);

  const marcarAssistido = useCallback(
    async (treinamentoId) => {
      if (progressoRef.current[treinamentoId]) return;
      // Optimistic update
      setProgresso((prev) => ({ ...prev, [treinamentoId]: true }));
      const { error } = await supabase.from("liberty_treinamento_progresso").insert({
        vigilante_id:   user?.id,
        vigilante_nome: user?.nome || null,
        treinamento_id: treinamentoId,
        assistido:      true,
        assistido_em:   new Date().toISOString(),
        empresa_id:     EMPRESA_ID,
      });
      if (error && error.code !== "23505") {
        console.error("[Treinamento] progresso insert error:", error.message);
      }
    },
    [user?.id, user?.nome]
  );
  marcarRef.current = marcarAssistido;

  // Single global listener for YouTube postMessage — set up once
  useEffect(() => {
    const handler = (e) => {
      if (!e.data || !videoAbertoRef.current) return;
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        const ended =
          (data?.event === "onStateChange" && data?.info === 0) ||
          (data?.event === "infoDelivery" && data?.info?.playerState === 0);
        if (ended) marcarRef.current?.(videoAbertoRef.current.id);
      } catch (_) {}
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const assistidos  = treinamentos.filter((t) => progresso[t.id]).length;
  const total       = treinamentos.length;
  const pct         = total > 0 ? Math.round((assistidos / total) * 100) : 0;
  const completo    = assistidos === total && total > 0;

  return (
    <MobileLayout title="Treinamento" scrollable>
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-[#0d1b3e] to-[#0a1128] border border-white/10 p-5 mb-4">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="tiny-label text-blue-200">Capacitação</p>
            <h2 className="font-display font-bold text-lg mt-1">Treinamentos</h2>
            <p className="text-xs text-slate-400 mt-1">Assista até o final para registrar como concluído</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-blue-300" />
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-slate-400">{assistidos} de {total} vídeos assistidos</span>
          <span className="font-bold text-blue-300">{pct}%</span>
        </div>
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${completo ? "bg-emerald-500" : "bg-blue-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Completion banner */}
      {completo && (
        <div className="rounded-2xl bg-emerald-500/15 border border-emerald-500/40 p-4 mb-4 text-center">
          <Trophy className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <p className="font-bold text-emerald-300 text-base">Parabéns! Treinamento concluído!</p>
          <p className="text-xs text-emerald-500 mt-1">Você assistiu todos os vídeos do programa.</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 text-center">
          <p className="text-sm text-slate-500 italic">Carregando treinamentos…</p>
        </div>
      )}

      {/* Video list */}
      <div className="space-y-3">
        {treinamentos.map((t) => {
          const videoId  = getYouTubeId(t.youtube_url);
          const assistido = !!progresso[t.id];

          return (
            <div
              key={t.id}
              onClick={() => setVideoAberto(t)}
              className={`rounded-2xl border p-4 cursor-pointer transition active:scale-[0.99] ${
                assistido
                  ? "bg-emerald-500/8 border-emerald-500/30"
                  : "bg-[#0a1128] border-white/10 hover:brightness-125"
              }`}
            >
              {/* Thumbnail */}
              {videoId && (
                <div
                  className="relative w-full rounded-xl overflow-hidden mb-3"
                  style={{ aspectRatio: "16/9" }}
                >
                  <img
                    src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                    alt={t.titulo}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <div className="w-12 h-12 rounded-full bg-white/20 border-2 border-white/60 flex items-center justify-center">
                      <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                  {assistido && (
                    <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500 text-white text-[10px] font-bold">
                      <CheckCircle2 className="w-3 h-3" /> Assistido
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold leading-snug ${assistido ? "text-emerald-200" : "text-white"}`}>
                    {t.titulo}
                  </p>
                  {t.descricao && (
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.descricao}</p>
                  )}
                </div>
                {assistido ? (
                  <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 text-[11px] font-semibold whitespace-nowrap shrink-0">
                    <CheckCircle2 className="w-3 h-3" /> Concluído
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-500/20 text-blue-300 text-[11px] font-semibold whitespace-nowrap shrink-0">
                    <Play className="w-3 h-3" /> Assistir
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Video player modal */}
      {videoAberto && (
        <VideoModal
          treinamento={videoAberto}
          assistido={!!progresso[videoAberto.id]}
          onClose={() => setVideoAberto(null)}
        />
      )}
    </MobileLayout>
  );
}

// ─── Video modal ──────────────────────────────────────────────────────────────

const VideoModal = ({ treinamento, assistido, onClose }) => {
  const videoId = getYouTubeId(treinamento.youtube_url);
  const [marcado, setMarcado] = useState(assistido);

  // Listen to state changes coming from the parent component
  useEffect(() => { setMarcado(assistido); }, [assistido]);

  if (!videoId) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/90 flex flex-col items-center justify-center p-8 text-center">
        <p className="text-slate-300 mb-4">URL de vídeo inválida.</p>
        <button onClick={onClose} className="px-4 py-2 rounded-xl bg-white/10 text-white text-sm">Fechar</button>
      </div>
    );
  }

  const embedUrl = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1&rel=0&origin=${encodeURIComponent(window.location.origin)}`;

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-black/80">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{treinamento.titulo}</p>
          {marcado ? (
            <p className="text-[11px] text-emerald-400 flex items-center gap-1 mt-0.5">
              <CheckCircle2 className="w-3 h-3" /> Marcado como assistido
            </p>
          ) : (
            <p className="text-[11px] text-slate-500 mt-0.5">Assista até o final para registrar</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Player */}
      <div className="flex-1 flex items-center bg-black">
        <div className="w-full" style={{ aspectRatio: "16/9" }}>
          <iframe
            src={embedUrl}
            title={treinamento.titulo}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-black/80 text-center">
        <p className="text-xs text-slate-600">
          {marcado ? "✓ Concluído — você pode fechar" : "O progresso é registrado automaticamente ao final do vídeo"}
        </p>
      </div>
    </div>
  );
};
